import CheckIcon from "@mui/icons-material/Check";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { CRAFT_COLORS } from "../models/Craft";

type Props = {
	value: string;
	onChange: (color: string) => void;
};

type SwatchProps = {
	color: string;
	selected: boolean;
	onClick: () => void;
};

const Swatch = ({ color, selected, onClick }: SwatchProps) => (
	<Box
		component="button"
		type="button"
		aria-label={color}
		aria-pressed={selected}
		onClick={onClick}
		sx={{
			width: "100%",
			aspectRatio: "1",
			borderRadius: "4px",
			border: "none",
			padding: 0,
			cursor: "pointer",
			background: color,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			color: "#fff",
			outline: selected ? "2px solid" : "none",
			outlineColor: "text.primary",
			outlineOffset: "2px",
			transition: "transform 120ms ease",
			"&:hover": { transform: "scale(1.08)" },
		}}
	>
		{selected && <CheckIcon sx={{ fontSize: 18 }} />}
	</Box>
);

export const CraftColorPicker = ({ value, onChange }: Props) => {
	const current = (value || "").toLowerCase();
	// A craft saved before this palette existed may carry another colour. It is kept apart,
	// so the palette grid stays even, it can be picked again after trying another one, and
	// opening and saving the craft does not change it.
	const [legacy, setLegacy] = useState("");
	useEffect(() => {
		if (current !== "" && !CRAFT_COLORS.includes(current)) setLegacy(current);
	}, [current]);

	return (
		<Box>
			<Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
				Color
			</Typography>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "repeat(6, 1fr)", sm: "repeat(12, 1fr)" },
					gap: 1,
				}}
			>
				{CRAFT_COLORS.map((color) => (
					<Swatch
						key={color}
						color={color}
						selected={color === current}
						onClick={() => onChange(color)}
					/>
				))}
			</Box>
			{legacy && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
					<Box sx={{ width: 28 }}>
						<Swatch
							color={legacy}
							selected={legacy === current}
							onClick={() => onChange(legacy)}
						/>
					</Box>
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						Current color, not in the palette
					</Typography>
				</Box>
			)}
		</Box>
	);
};
